/**
 * `overlayToAddressDecoration` — the ONE reducer from a precedence-RESOLVED
 * {@link ResolvedOverlays} bundle down to the neutral {@link AddressOverlay}
 * decoration the transpose adapters (graph / scene) stamp onto a node.
 *
 * Every surface that paints an address with its catalog identity — the tx graph
 * nodes, the semantic scene, the flow graph, the address panel/header chips —
 * goes through here, so the "which label / logo / colour / entity wins" choice
 * is made in exactly ONE place and can't drift between surfaces. The overlays
 * are already chain-scoped by the fetch (the batch hook resolves per
 * `(chain, address)`), so this is a pure pick over the resolved bundle.
 *
 * Precedence within the bundle: a curated PRIMARY of a kind wins over the merge
 * leader, which wins over the first hit. Brand (logo/colour) prefers the primary
 * entity's flavour, then the leading label, then the leading tag.
 *
 * RISK is the exception to that precedence, deliberately: it takes the WORST
 * severity across every label and tag rather than the leading one. A curated
 * primary exists to decide what an address is *called*, and letting it decide
 * how dangerous the address is would let "Exchange" outrank "Fake_Phishing" on
 * an address carrying both.
 */
import { addressProxySummary, addressRisk, catalogRisk, type RiskBearingRow } from "@authe/fe-commons/overlays";
import type { AddressOverlay } from "@authe/fe-commons/transpose";
import type {
  ResolvedCatalogHit,
  ResolvedOverlayEntity,
  ResolvedOverlays,
} from "@authe/fe-commons/overlays";

function primaryIdFor(overlays: ResolvedOverlays, kind: string): string | undefined {
  return overlays.primary.find((p) => p.kind === kind)?.catalogId;
}

/** The winning hit for a kind: curated primary → merge-flagged primary → first. */
function leadingHit(
  hits: ResolvedCatalogHit[],
  primaryId: string | undefined,
): ResolvedCatalogHit | undefined {
  if (primaryId) {
    const match = hits.find((h) => h.catalogId === primaryId);
    if (match) return match;
  }
  return hits.find((h) => h.isPrimary) ?? hits[0];
}

function leadingEntity(overlays: ResolvedOverlays): ResolvedOverlayEntity | undefined {
  const primaryId = primaryIdFor(overlays, "entity");
  if (primaryId) {
    const match = overlays.entities.find((e) => e.id === primaryId);
    if (match) return match;
  }
  return overlays.entities[0];
}

export interface OverlayDecorationOptions {
  /**
   * Turn a stored logo key into a renderable URL (the catalog logos are S3 keys,
   * not URLs). Pass `catalog.resolveLogoUrl`; when omitted the raw key is kept
   * (fine for already-absolute URLs, best-effort otherwise).
   */
  resolveLogoUrl?: (key: string) => string | undefined;
  /**
   * The address's stored row, when the batch was asked for one.
   *
   * Carries the half of risk the catalog cannot see: a band an analyst set by
   * hand. Without it a node whose whole risk is somebody's own judgement drew
   * exactly like an unassessed one — the analyst set `severe` on the address and
   * the graph did not change colour, which is the one place colour IS the
   * signal.
   */
  row?: DecorationRow | null;
}

/**
 * The stored row as this reducer reads it: risk, plus the registry's own NAME.
 *
 * `RiskBearingRow` is the narrow risk view; the value every caller actually
 * passes is the whole `Address`, so the name is already in hand.
 */
export type DecorationRow = NonNullable<RiskBearingRow> & { name?: unknown; metadata?: Record<string, unknown>; is_contract_verified?: unknown };

/**
 * The registry's name for the address, when it says anything.
 *
 * This is the field the address page titles itself with (`AddressDetailPage`'s
 * `headerName`), and the reason a decoded argument could read as bare hex on a
 * page whose own Addresses card, and whose address page one click away, both
 * said "Wrapped Ether": the name lives on the ROW, and only the catalog
 * overlays were being reduced. A row is only fetched with `includeRow`, so this
 * costs nothing where no caller asked for one.
 *
 * Skipped when it merely restates the address — some providers fill the name
 * with the hex — which would render as a second copy of what is already there.
 */
function rowName(row: DecorationRow | null | undefined): string | undefined {
  const name = typeof row?.name === "string" ? row.name.trim() : "";
  if (!name) return undefined;
  const bare = name.replace(/^0x/i, "").toLowerCase();
  return /^[0-9a-f]{40,64}$/.test(bare) ? undefined : name;
}

/**
 * Collapse a resolved bundle into the node decoration. Returns `undefined` when
 * the bundle carries nothing to paint, so callers can leave a node bare rather
 * than stamp an empty overlay.
 */
export function overlayToAddressDecoration(
  overlays: ResolvedOverlays | null | undefined,
  opts: OverlayDecorationOptions = {},
): AddressOverlay | undefined {
  // A row alone is enough to decorate with: an address the catalog has nothing
  // curated about still has a registry name and a recorded risk band, and
  // returning early on the empty bundle is what dropped both.
  if (!overlays && !opts.row) return undefined;
  const leadLabel = overlays ? leadingHit(overlays.labels, primaryIdFor(overlays, "label")) : undefined;
  const leadTag = overlays ? leadingHit(overlays.tags, primaryIdFor(overlays, "tag")) : undefined;
  const entity = overlays ? leadingEntity(overlays) : undefined;

  const tags = Array.from(
    new Set((overlays?.tags ?? []).map((t) => t.name).filter((n): n is string => Boolean(n))),
  );
  // Curated attribution first — a label or an entity is somebody's statement
  // about what this address IS — then the registry's plain name.
  const label = leadLabel?.name ?? entity?.name ?? rowName(opts.row);
  const color = entity?.color ?? leadLabel?.color ?? leadTag?.color;
  const rawLogo = entity?.logo ?? leadLabel?.logo ?? leadTag?.logo;
  const logo = rawLogo ? (opts.resolveLogoUrl?.(rawLogo) ?? rawLogo) : undefined;
  const primaryEntityId = entity?.id;

  const decoration: AddressOverlay = {};
  // Never infer source verification from catalog lifecycle, an ABI or a proxy target.
  if (opts.row?.is_contract_verified === true) decoration.contractVerified = true;
  const proxy = addressProxySummary(opts.row?.metadata?.proxy);
  if (proxy) decoration.proxy = proxy;
  if (label) decoration.label = label;
  if (tags.length > 0) decoration.tags = tags;
  if (color) decoration.color = color;
  if (logo) decoration.logo = logo;
  if (primaryEntityId) decoration.primaryEntityId = primaryEntityId;
  // Its NAME too, not only its id — see `AddressOverlay.primaryEntityName`. It
  // is already resolved right here; only the id was being carried, which left
  // every entity band on a graph titled with a hash.
  if (entity?.name) decoration.primaryEntityName = entity.name;
  // Labels AND tags: an accusation arrives as either, depending on which
  // explorer chip the scraper read it from. Then the worst of that and the row's
  // own recorded band — `addressRisk` is the same rule the address page header
  // uses, so a node and that page cannot disagree about one address.
  const risk = addressRisk([opts.row], catalogRisk(overlays));
  if (risk) {
    decoration.riskLevel = risk.level;
    // Both, because `GraphNode` renders both: a badge showing "High" with a
    // blank beside it reads as a number that failed to load rather than one
    // nobody claimed.
    decoration.riskScore = risk.score;
  }

  return Object.keys(decoration).length > 0 ? decoration : undefined;
}
